import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, X, Check } from "lucide-react";
import { usePremium } from "../context/PremiumContext";

const DEFAULT_PERKS = [
  "Explain any verse or chapter",
  "Onyx audio narration",
  "Devotionals, plans & Word Study",
  "NKJV translation",
];
const DAY = 86400000;

// Shows when a user taps a gated feature, and occasionally (tactfully) as a
// recurring invite. In-app + non-premium only.
export default function UpgradeDialog() {
  const { inApp, isPremium, isLegacy } = usePremium();
  const navigate = useNavigate();
  const [state, setState] = useState(null);

  const open = useCallback((feature, perks) => {
    if (!inApp || isPremium) return;
    setState({
      feature: feature && feature !== "premium" ? feature : null,
      perks: perks && perks.length ? perks : DEFAULT_PERKS,
    });
  }, [inApp, isPremium]);

  useEffect(() => {
    const h = (e) => open(e.detail?.feature, e.detail?.perks);
    window.addEventListener("cb-show-upgrade", h);
    return () => window.removeEventListener("cb-show-upgrade", h);
  }, [open]);

  // Recurring, gentle nudge — at most once every few days, never during first-run.
  useEffect(() => {
    if (!inApp || isPremium || isLegacy) return;
    try {
      if (localStorage.getItem("cb_tour_done") !== "1") return;
      const opens = (parseInt(localStorage.getItem("cb_open_count") || "0", 10) || 0) + 1;
      localStorage.setItem("cb_open_count", String(opens));
      const last = parseInt(localStorage.getItem("cb_nudge_last") || "0", 10) || 0;
      if (opens >= 3 && Date.now() - last > 3 * DAY) {
        localStorage.setItem("cb_nudge_last", String(Date.now()));
        const t = setTimeout(() => open("premium"), 1500);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state) return null;
  const close = () => setState(null);
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-5 backdrop-blur-sm" data-testid="upgrade-dialog">
      <div className="relative w-full max-w-sm rounded-3xl border border-border bg-background p-7 text-center shadow-2xl">
        <button onClick={close} data-testid="upgrade-close" className="absolute right-4 top-4 text-muted-foreground transition-colors hover:text-foreground">
          <X className="h-5 w-5" />
        </button>
        <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15 text-primary">
          <Crown className="h-6 w-6" />
        </span>
        <h2 className="font-serif-display text-2xl tracking-tight">
          {state.feature ? `Unlock ${state.feature}` : "Go deeper with Premium"}
        </h2>
        <p className="font-ui mt-2 text-sm text-muted-foreground">
          Reading is always free. Start your <span className="font-semibold text-primary">3-day free trial</span> — cancel anytime.
        </p>
        <ul className="mx-auto mt-5 max-w-xs space-y-2 text-left">
          {state.perks.map((p, i) => (
            <li key={i} className="font-ui flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 shrink-0 text-primary" /> {p}
            </li>
          ))}
        </ul>
        <button
          onClick={() => { close(); navigate("/premium"); }}
          data-testid="upgrade-cta"
          className="font-ui mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"
        >
          <Crown className="h-4 w-4" /> Start free trial
        </button>
        <button onClick={close} data-testid="upgrade-later" className="font-ui mt-3 w-full text-sm text-muted-foreground hover:text-foreground">
          Not now
        </button>
      </div>
    </div>
  );
}
